import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import 'dayjs/locale/pt-br';
import {
  Card,
  CardHeader,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Stack,
  Autocomplete,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { Ban, CalendarCheck, Check } from 'lucide-react';
import Loading from '../components/loading';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';
import ProfessionalModal from '../register/professional/professionalModal';
import ConsultationTypeFormModal from '../register/consultationTypes/consultationTypeFormModal';
import CancelAppointmentDialog from './cancelAppointmentDialog';
import AppointmentHistoryModal from './appointmentHistoryModal';
import { useAppointment } from './hooks/useAppointment';
import { useSaveAppointment } from './hooks/useSaveAppointment';
import { useCheckProfessionalData } from './hooks/useCheckProfessionalData';
import { useAppointmentActions } from './hooks/useAppointmentActions';
import { usePatientsComboBox } from '../hooks/usePatientsComboBox';
import { useProfessionals } from '../hooks/useProfessionals';
import { useProfessionalOptions, professionalOptionLabel } from './hooks/useProfessionalOptions';
import { useAppointmentTypes } from '../hooks/useAppointmentTypes';
import { useRooms } from '../hooks/useRooms';
import { useRoomLabel } from '../hooks/useRoomLabel';

const PROFESSIONAL_STORAGE_KEY = 'lastSelectedProfessionalId';
const ROOM_STORAGE_KEY = 'lastSelectedRoomId';

function AppointmentMobile() {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const location = useLocation();
  const { appointmentData } = location.state || {};
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [isDentistModalOpen, setDentistModalOpen] = useState(false);
  const [isAppointmentTypeModalOpen, setAppointmentTypeModalOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isTypeSelectOpen, setTypeSelectOpen] = useState(false);
  const [showProfessionalWarningDialog, setShowProfessionalWarningDialog] = useState(false);
  const [professionalWarningMessage, setProfessionalWarningMessage] = useState('');
  const [appointmentDate, setAppointmentDate] = useState(dayjs());
  const [appointmentHour, setAppointmentHour] = useState(dayjs());
  const [appointmentEndHour, setAppointmentEndHour] = useState(null);
  const [appointmentObservation, setAppointmentObservation] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [appointmentStatus, setAppointmentStatus] = useState('');
  const [appointmentStatusDescription, setAppointmentStatusDescription] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const canReactivate = ['CANCELED', 'PERFORMED', 'NO_SHOW'].includes(appointmentStatus);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const { patients: comboBoxPatients } = usePatientsComboBox();
  const { professionals: comboBoxDentists, refetch: refetchDentists } = useProfessionals();
  const { appointmentTypes, refetch: refetchAppointmentTypes } = useAppointmentTypes();
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();
  const {
    appointment,
    isLoading: isAppointmentLoading,
    isStale: isAppointmentStale,
    updatedAt: appointmentUpdatedAt,
    refetch: refetchAppointment,
  } = useAppointment(appointmentId);
  const dentistOptions = useProfessionalOptions(comboBoxDentists, appointment);
  const { save: saveAppointmentRequest } = useSaveAppointment();
  const { check: checkProfessionalData } = useCheckProfessionalData();
  const { confirm, cancel, reactivate } = useAppointmentActions();

  useEffect(() => {
    if (!appointmentId) {
      setAppointmentDate(dayjs(appointmentData?.date));
      setAppointmentHour(dayjs(appointmentData?.hour, 'HH:mm'));
      setSelectedRoomId(appointmentData?.roomId ?? '');
    }
  }, [appointmentData, appointmentId]);

  // Pré-preenche profissional e sala com a última seleção salva ao criar uma
  // consulta (inclusive vindo do calendário). Só preenche o que estiver vazio:
  // o profissional do usuário logado e a sala vinda do calendário têm
  // prioridade. Os efeitos dependem das listas porque o id salvo é string e
  // precisa ser resolvido (e validado) contra as opções carregadas.
  useEffect(() => {
    if (appointmentId) {
      return;
    }
    const savedProfessionalId = localStorage.getItem(PROFESSIONAL_STORAGE_KEY);
    const professional = comboBoxDentists.find((d) => String(d.id) === savedProfessionalId);
    if (professional) {
      setSelectedProfessionalId((current) => current || professional.id);
    }
  }, [appointmentId, comboBoxDentists]);

  useEffect(() => {
    if (appointmentId) {
      return;
    }
    const savedRoomId = localStorage.getItem(ROOM_STORAGE_KEY);
    const room = rooms.find((r) => String(r.id) === savedRoomId);
    if (room) {
      setSelectedRoomId((current) => current || room.id);
    }
  }, [appointmentId, rooms]);

  useEffect(() => {
    if (!appointment) {
      return;
    }
    setSelectedPatient({ id: appointment.pacienteId, label: appointment.nomeDoPaciente });
    setSelectedProfessionalId(appointment.dentistaId);
    setSelectedRoomId(appointment.roomId ?? '');
    setAppointmentDate(dayjs(appointment.data, 'DD/MM/YYYY'));
    setAppointmentHour(dayjs(appointment.hora, 'HH:mm'));
    setAppointmentEndHour(dayjs(appointment.hora, 'HH:mm').add(appointment.duracao, 'minute'));
    setAppointmentObservation(appointment.observacao);
    setAppointmentType(appointment.appointmentTypeId ?? '');
    setAppointmentStatusDescription(appointment.descricaoDoStatus);
    setAppointmentStatus(appointment.status);
    setCancellationReason(appointment.cancellationReason ?? '');
  }, [appointment]);

  const buildAppointment = () => ({
    pacienteId: selectedPatient.id,
    dentistaId: selectedProfessionalId,
    roomId: selectedRoomId,
    data: dayjs(appointmentDate).format('DD/MM/YYYY'),
    hora: dayjs(appointmentHour).format('HH:mm'),
    horaFim: dayjs(appointmentEndHour).format('HH:mm'),
    duracao: dayjs(appointmentEndHour).diff(dayjs(appointmentHour), 'minute'),
    observacao: appointmentObservation,
    appointmentTypeId: appointmentType,
    id: appointmentId,
  });

  const saveAppointment = (event) => {
    event.preventDefault();
    setLoading(true);

    if (!validateRequiredFields()) {
      return;
    }

    const appointment = buildAppointment();

    checkProfessionalData(appointment)
      .then((returnFromApi) => {
        const warningMessage = returnFromApi.data?.message;
        if (warningMessage) {
          setLoading(false);
          setProfessionalWarningMessage(warningMessage);
          setShowProfessionalWarningDialog(true);
        } else {
          persistAppointment(appointment);
        }
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const persistAppointment = (appointment) => {
    setLoading(true);

    if (appointmentId) {
      saveAppointmentRequest(appointment)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data);
        })
        .catch((returnFromApi) => {
          setLoading(false);
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          console.error(returnFromApi);
        });
    } else {
      saveAppointmentRequest(appointment)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data.message);
          cleanFields();
        })
        .catch((returnFromApi) => {
          setLoading(false);
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          console.error(returnFromApi);
        });
    }
  };

  const confirmProfessionalWarning = () => {
    setShowProfessionalWarningDialog(false);
    persistAppointment(buildAppointment());
  };

  const closeProfessionalWarning = () => {
    setShowProfessionalWarningDialog(false);
    setProfessionalWarningMessage('');
  };

  function validateRequiredFields() {
    if (
      !selectedPatient ||
      !selectedProfessionalId ||
      !appointmentType ||
      !appointmentEndHour ||
      !appointmentHour ||
      !appointmentDate
    ) {
      setShowErrorMessage(true);
      setErrorMessage(
        'Para salvar uma consulta é obrigatório preencher os campos: Paciente, Profissional, Data, Hora de Início, Hora de Fim e Tipo',
      );
      setLoading(false);
      return false;
    }
    return true;
  }

  function requireSavedAppointment(action) {
    if (appointmentId) return true;
    setShowErrorMessage(true);
    setErrorMessage(`Salve a consulta antes de ${action}.`);
    return false;
  }

  function openCancelDialog() {
    if (!requireSavedAppointment('cancelá-la')) return;
    setShowCancelDialog(true);
  }

  function reactivateAppointment() {
    if (!appointmentId) {
      setShowErrorMessage(true);
      setErrorMessage('Salve a consulta antes de reativá-la.');
      return;
    }
    setLoading(true);
    reactivate(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function confirmAppointment() {
    if (!requireSavedAppointment('confirmá-la')) return;
    setLoading(true);
    confirm(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function cancelAppointment(reason) {
    setLoading(true);
    cancel(appointmentId, reason)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setShowCancelDialog(false);
        refetchAppointment();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function cleanFields() {
    const savedProfessionalId = localStorage.getItem(PROFESSIONAL_STORAGE_KEY);
    const savedRoomId = localStorage.getItem(ROOM_STORAGE_KEY);
    setSelectedProfessionalId(
      comboBoxDentists.find((d) => String(d.id) === savedProfessionalId)?.id ?? '',
    );
    setSelectedRoomId(rooms.find((room) => String(room.id) === savedRoomId)?.id ?? '');
    setSelectedPatient('');
    setAppointmentDate(dayjs());
    setAppointmentHour(dayjs());
    setAppointmentEndHour(null);
    setAppointmentObservation('');
    setAppointmentType('');
  }

  function goToMedicalRecord() {
    if (!selectedPatient?.id) {
      setShowErrorMessage(true);
      setErrorMessage('Selecione um paciente para abrir o prontuário.');
      return;
    }
    navigate(`/patient-form/${selectedPatient.id}`);
  }

  function handleNewDentistSaved() {
    refetchDentists();
  }

  function handleNewAppointmentTypeClick() {
    setTypeSelectOpen(false);
    setAppointmentTypeModalOpen(true);
  }

  function handleNewAppointmentTypeSaved() {
    refetchAppointmentTypes();
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
        <CardHeader
          title="Consulta"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: 'text.secondary' }}
        />

        <Loading isToHidden={isLoading === false && isAppointmentLoading === false} />
        {showSuccessMessage && (
          <SuccessMessage
            message={successMessage}
            show={showSuccessMessage}
            setShow={setShowSuccessMessage}
          />
        )}
        {showErrorMessage && (
          <ErrorMessage
            message={errorMessage}
            show={showErrorMessage}
            setShow={setShowErrorMessage}
          />
        )}

        <CardContent>
          {isAppointmentStale && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Sem conexão com o servidor. Exibindo os últimos dados salvos
              {appointmentUpdatedAt
                ? ` (${dayjs(appointmentUpdatedAt).format('DD/MM/YYYY HH:mm')})`
                : ''}
              . Alterações não podem ser salvas offline.
            </Alert>
          )}
          <Box component="form" onSubmit={saveAppointment}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                {appointmentStatus === 'SCHEDULED' && (
                  <Chip
                    label={appointmentStatusDescription}
                    icon={<CalendarCheck size={15} />}
                    color="info"
                  />
                )}
                {appointmentStatus === 'CONFIRMED' && (
                  <Chip
                    label={appointmentStatusDescription}
                    icon={<Check size={15} />}
                    color="primary"
                  />
                )}
                {appointmentStatus === 'PERFORMED' && (
                  <Chip
                    label={appointmentStatusDescription}
                    icon={<Check size={15} />}
                    color="success"
                  />
                )}
                {appointmentStatus === 'CANCELED' && (
                  <Chip
                    label={appointmentStatusDescription}
                    icon={<Ban size={15} />}
                    color="error"
                  />
                )}
                {appointmentId && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    sx={{ ml: 'auto !important' }}
                    data-cy="appointment-history-link"
                    onClick={() => setShowHistoryModal(true)}
                  >
                    Ver Histórico
                  </Link>
                )}
              </Stack>
              {appointmentStatus === 'CANCELED' && cancellationReason && (
                <Alert severity="error" data-cy="cancellation-reason">
                  <strong>Motivo do cancelamento:</strong> {cancellationReason}
                </Alert>
              )}
              {appointment?.reminderSentAt && (
                <Alert severity="info" data-cy="reminder-sent-at">
                  <strong>Lembrete enviado ao paciente em:</strong> {appointment.reminderSentAt}
                </Alert>
              )}

              <FormControl fullWidth>
                <Autocomplete
                  data-cy="patient-auto-complete"
                  options={comboBoxPatients}
                  getOptionLabel={(option) => option.label ?? ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} label="Paciente" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                  value={selectedPatient}
                  onChange={(_, newValue) => setSelectedPatient(newValue ?? null)}
                />
              </FormControl>

              <FormControl fullWidth>
                <Autocomplete
                  options={dentistOptions}
                  getOptionLabel={professionalOptionLabel}
                  getOptionDisabled={(option) => Boolean(option.inactive)}
                  value={dentistOptions.find((d) => d.id === selectedProfessionalId) ?? null}
                  onChange={(_, newValue) => {
                    setSelectedProfessionalId(newValue?.id ?? '');
                    setSelectedRoomId(newValue?.roomId ?? '');
                    localStorage.setItem(PROFESSIONAL_STORAGE_KEY, newValue?.id ?? '');
                    localStorage.setItem(ROOM_STORAGE_KEY, newValue?.roomId ?? '');
                  }}
                  noOptionsText="Nenhum profissional encontrado."
                  PaperComponent={({ children, ...other }) => (
                    <Paper {...other}>
                      {children}
                      <Box
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <Button fullWidth size="small" onClick={() => setDentistModalOpen(true)}>
                          + Adicionar profissional
                        </Button>
                      </Box>
                    </Paper>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      data-cy="dentist-select"
                      fullWidth
                      label="Profissional"
                    />
                  )}
                />
              </FormControl>

              {rooms.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel id="room-combobox-label">{roomLabel.singular}</InputLabel>
                  <Select
                    labelId="room-combobox-label"
                    id="room-combobox"
                    value={selectedRoomId}
                    label={roomLabel.singular}
                    onChange={(e) => {
                      setSelectedRoomId(e.target.value);
                      localStorage.setItem(ROOM_STORAGE_KEY, e.target.value);
                    }}
                  >
                    {rooms.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <DatePicker
                label="Data"
                value={appointmentDate}
                onChange={(newValue) => setAppointmentDate(newValue)}
              />

              <TimeField
                label="Hora de Início"
                value={appointmentHour}
                onChange={(newValue) => setAppointmentHour(newValue)}
              />

              <TimeField
                label="Hora de Fim"
                value={appointmentEndHour}
                onChange={(newValue) => setAppointmentEndHour(newValue)}
              />

              <TextField
                label="Observações"
                multiline
                rows={4}
                value={appointmentObservation}
                onChange={(e) => setAppointmentObservation(e.target.value)}
              />

              <FormControl fullWidth>
                <InputLabel id="type-combobox-label">Tipo</InputLabel>
                <Select
                  labelId="type-combobox-label"
                  id="type-combobox"
                  value={appointmentType}
                  label="Tipo"
                  MenuProps={{ MenuListProps: { sx: { pb: 0 } } }}
                  open={isTypeSelectOpen}
                  onOpen={() => setTypeSelectOpen(true)}
                  onClose={() => setTypeSelectOpen(false)}
                  onChange={(e) => setAppointmentType(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Selecione o tipo</em>
                  </MenuItem>
                  {appointmentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.description}
                    </MenuItem>
                  ))}
                  <Box
                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Button fullWidth size="small" onClick={handleNewAppointmentTypeClick}>
                      + Novo tipo
                    </Button>
                  </Box>
                </Select>
              </FormControl>

              <Stack spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" type="submit">
                  Salvar
                </Button>
                <Button color="success" variant="contained" onClick={confirmAppointment}>
                  Confirmar
                </Button>
                <Button variant="outlined" onClick={goToMedicalRecord}>
                  Prontuário
                </Button>
                <Button color="error" variant="contained" onClick={openCancelDialog}>
                  Cancelar
                </Button>
                {canReactivate && (
                  <Button
                    data-cy="reactivate-appointment-btn"
                    variant="outlined"
                    onClick={reactivateAppointment}
                  >
                    Reativar
                  </Button>
                )}
                <Button variant="outlined" onClick={() => navigate('/agenda-router')}>
                  Voltar
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      <AppointmentHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        appointmentId={appointmentId}
      />
      <ProfessionalModal
        open={isDentistModalOpen}
        onClose={() => setDentistModalOpen(false)}
        onSave={handleNewDentistSaved}
      />
      <ConsultationTypeFormModal
        open={isAppointmentTypeModalOpen}
        onClose={() => setAppointmentTypeModalOpen(false)}
        onSave={handleNewAppointmentTypeSaved}
      />
      <CancelAppointmentDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={cancelAppointment}
      />
      <Dialog
        open={showProfessionalWarningDialog}
        onClose={closeProfessionalWarning}
        aria-labelledby="professional-warning-dialog-title"
        aria-describedby="professional-warning-dialog-description"
      >
        <DialogTitle id="professional-warning-dialog-title">{'Confirmar consulta'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="professional-warning-dialog-description">
            {professionalWarningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProfessionalWarning}>Cancelar</Button>
          <Button variant="contained" onClick={confirmProfessionalWarning}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AppointmentMobile;
